import { Enemy } from './Enemy.js'
import { PLAYER_DAMAGE } from '../game/CombatBalance.js'

export class DarkWizard extends Enemy {
  constructor(game, x, y, id) {
    super(game, x, y, id, 'darkWizard')
    
    this.maxHealth = 50
    this.health = 50
    this.damage = PLAYER_DAMAGE.STANDARD
    this.moveSpeed = 2.5
    this.attackRange = 12 // Ranged
    this.attackCooldown = 2.5
    this.detectionRange = 20
    
    this.projectiles = []
    this.teleportCooldown = 0
  }
  
  update(delta) {
    super.update(delta)
    
    this.teleportCooldown -= delta
    
    // Update projectiles (straight line, no homing)
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const proj = this.projectiles[i]
      
      // Move in straight line
      proj.x += proj.dirX * proj.speed * delta
      proj.y += proj.dirY * proj.speed * delta
      proj.lifetime -= delta
      
      // Check hit player
      const hitDist = Math.sqrt(
        Math.pow(proj.x - this.game.player.x, 2) +
        Math.pow(proj.y - this.game.player.y, 2)
      )
      
      if (hitDist < 0.5) {
        const actualDamage = this.damage * this.damageMultiplier
        this.game.player.takeDamage(actualDamage)
        this.projectiles.splice(i, 1)
        continue
      }
      
      // Remove expired or if hit wall
      if (proj.lifetime <= 0 || this.game.world.isSolid(proj.x, proj.y)) {
        this.projectiles.splice(i, 1)
      }
    }
  }
  
  updateAI(delta) {
    super.updateAI(delta)
    
    const player = this.game.player
    if (player) {
      const dx = player.x - this.x
      const dy = player.y - this.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      
      // Teleport away if player gets too close
      if (dist < 5 && this.teleportCooldown <= 0) {
        this.teleport()
      }
    }
  }
  
  teleport() {
    this.teleportCooldown = 5

    const player = this.game.player
    if (!player) return
    const roomId = this.game.world.getRoomIdForPosition(this.x, this.y)
    const awayAngle = Math.atan2(this.y - player.y, this.x - player.x)

    // Stay inside the active Graveyard room. The old unrestricted teleport
    // could place a wizard behind a wall or outside revealed tiles, making it
    // appear to vanish during the fight.
    for (let attempt = 0; attempt < 12; attempt++) {
      const angle = awayAngle + ((attempt % 2 ? -1 : 1) * Math.ceil(attempt / 2) * Math.PI / 8)
      const distance = 4 + (attempt % 3)
      const x = player.x + Math.cos(angle) * distance
      const y = player.y + Math.sin(angle) * distance
      if (this.game.world.getRoomIdForPosition(x, y) !== roomId) continue
      if (this.game.world.isSolid(x, y)) continue
      this.x = x
      this.y = y
      return
    }
  }
  
  attack() {
    const player = this.game.player
    if (!player) return
    
    const dx = player.x - this.x
    const dy = player.y - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    
    if (dist > 0) {
      this.projectiles.push({
        x: this.x,
        y: this.y,
        dirX: dx / dist,
        dirY: dy / dist,
        speed: 8,
        lifetime: 4
      })
    }
  }
  
  render(renderer) {
    super.render(renderer)
    
    // Render projectiles (magic orbs)
    for (const proj of this.projectiles) {
      renderer.fillCircle(proj.x, proj.y, 0.2, '#4488ff')
      renderer.fillCircle(proj.x, proj.y, 0.12, '#aaddff')
    }
  }
  
  die() {
    this.projectiles = []
    super.die()
  }
  
  dropLoot() {
    if (Math.random() < 0.35) {
      this.game.itemManager.spawnItem({
        type: 'potion',
        id: 'health_potion',
        name: 'Health Potion',
        healAmount: 50,
        count: 1
      }, { x: this.x, y: this.y })
    }
    
    if (Math.random() < 0.2) {
      this.game.itemManager.spawnItem({
        type: 'item',
        id: 'magic_crystal',
        name: 'Magic Crystal',
        count: 1
      }, { x: this.x, y: this.y })
    }
  }
}
